import React, { useEffect, useState, useCallback } from 'react';
import { useWorkspaceStore } from '../../stores/useWorkspaceStore.js';
import { storageRegistry } from '../../services/storage.js';
import FileTree from './FileTree.js';
import FileUpload from './FileUpload.js';
import { Folder, Upload } from 'lucide-react';

interface FilesPanelProps {
  className?: string;
}

export const FilesPanel: React.FC<FilesPanelProps> = ({ className = '' }) => {
  const storageProvider = useWorkspaceStore((state) => state.storageProvider);
  const currentPath = useWorkspaceStore((state) => state.currentPath);
  const files = useWorkspaceStore((state) => state.files);
  const isLoading = useWorkspaceStore((state) => state.isLoading);
  const error = useWorkspaceStore((state) => state.error);
  const selectedFile = useWorkspaceStore((state) => state.selectedFile);
  
  const setStorageProvider = useWorkspaceStore((state) => state.setStorageProvider);
  const setFiles = useWorkspaceStore((state) => state.setFiles);
  const setLoading = useWorkspaceStore((state) => state.setLoading);
  const setError = useWorkspaceStore((state) => state.setError);
  const navigateToPath = useWorkspaceStore((state) => state.navigateToPath);
  const selectFile = useWorkspaceStore((state) => state.selectFile);

  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const loadFiles = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const provider = storageRegistry.getProvider(storageProvider);
      if (!provider) {
        throw new Error(`Storage provider '${storageProvider}' not found`);
      }
      
      const fileList = await provider.list(path);
      setFiles(fileList);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load files';
      setError(errorMessage);
      console.error('Failed to load files:', err);
    } finally {
      setLoading(false);
    }
  }, [storageProvider, setFiles, setLoading, setError]);

  useEffect(() => {
    loadFiles(currentPath);
  }, [storageProvider, currentPath, loadFiles]);

  useEffect(() => {
    const pathParts = currentPath.split('/').filter(Boolean);
    setBreadcrumbs(['/', ...pathParts]);
  }, [currentPath]);

  const handleProviderChange = (provider: string) => {
    setStorageProvider(provider);
    navigateToPath('/');
  };

  const handleFileSelect = (filePath: string) => {
    const file = files.find(f => f.path === filePath);
    if (file) {
      if (file.type === 'folder') {
        navigateToPath(filePath);
      } else {
        selectFile(filePath);
      }
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === 0) {
      navigateToPath('/');
    } else {
      const newPath = '/' + breadcrumbs.slice(1, index + 1).join('/');
      navigateToPath(newPath);
    }
  };



  const availableProviders = storageRegistry.getAvailableProviders();

  return (
    <div className={`h-full flex flex-col bg-slate-800 ${className}`}>
      {/* Provider Selection Header */}
      <div className="p-3 border-b border-slate-600">
        <div className="flex items-center gap-2 mb-2">
          <Folder className="w-5 h-5 text-blue-400" />
          <span className="text-sm font-medium text-gray-200">Storage Provider</span>
        </div>
        <select 
          value={storageProvider}
          onChange={(e) => handleProviderChange(e.target.value)}
          className="w-full p-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          disabled={isLoading}
        >
          {availableProviders.map(({ key, name }) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* Breadcrumb Navigation */}
      {breadcrumbs.length > 1 && (
        <div className="p-3 border-b border-slate-600">
          <div className="flex items-center gap-1 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                  disabled={isLoading}
                >
                  {crumb === '/' ? 'Root' : crumb}
                </button>
                {index < breadcrumbs.length - 1 && (
                  <span className="text-gray-500">/</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-900/20 border-b border-red-700/30">
          <div className="text-red-400 text-sm">
            <span className="font-medium">Error:</span> {error}
          </div>
        </div>
      )}

      {/* File Tree */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 text-center">
            <div className="inline-flex items-center gap-2 text-gray-400">
              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">Loading files...</span>
            </div>
          </div>
        ) : files.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No files found in this directory</p>
          </div>
        ) : (
          <FileTree 
            files={files.map(file => ({
              name: file.name,
              path: file.path,
              type: file.type,
              size: file.size,
              lastModified: file.lastModified,
              scanStatus: file.scanStatus || 'unknown'
            }))}
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile || undefined}
          />
        )}
      </div>

      {/* File Upload Section */}
      <div className="p-3 border-t border-slate-600">
        <div className="flex items-center gap-2 mb-2">
          <Upload className="w-4 h-4 text-green-400" />
          <span className="text-sm font-medium text-gray-200">Upload Files</span>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded-lg transition-colors text-sm"
        >
          <Upload className="w-4 h-4" />
          <span>Upload Files</span>
        </button>
        
        <FileUpload 
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
        />
      </div>

      {/* Current Selection Info */}
      {selectedFile && (
        <div className="p-3 border-t border-slate-600 bg-slate-700/50">
          <div className="text-xs text-gray-400">Selected:</div>
          <div className="text-sm text-white truncate" title={selectedFile}>
            {selectedFile.split('/').pop()}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesPanel;
